import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfWeek, addDays, isSameDay, addWeeks, subWeeks, parseISO } from "date-fns";
import { fi } from "date-fns/locale";
import { getCustomerDisplayName } from "@/lib/formatters";
import { 
  INSTALLATION_STATUS_LABELS, 
  INSTALLATION_STATUS_BADGE_COLORS,
  TASK_TYPE_LABELS,
} from "@/config/constants";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, MapPin, User, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { InstallationFull } from "@/types/installations";
import { CalendarSkeleton } from "@/components/calendar/CalendarSkeleton";

interface Installer {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

// Status labels and colors are now imported from @/config/constants

const taskTypeColors: Record<string, string> = {
  installation: "bg-primary/10",
  delivery: "bg-blue-500/10",
  maintenance: "bg-purple-500/10",
  pickup: "bg-orange-500/10",
};

export default function CalendarPage() {
  const [currentWeekStart, setCurrentWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedInstaller, setSelectedInstaller] = useState<string>("all");

  // Generate week days
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  const { data: installers, isLoading: isLoadingInstallers } = useQuery({
    queryKey: ["installers"],
    queryFn: async () => {
      // 1. Hae asentaja-roolin user_id:t
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "installer");

      if (roleError) throw roleError;

      const installerIds = roleData?.map(r => r.user_id) || [];
      if (installerIds.length === 0) return [];

      // 2. Hae profiilitiedot
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .in("id", installerIds)
        .is("deleted_at", null);

      if (error) throw error;
      return data as Installer[];
    },
  });

  const { data: installations, isLoading: isLoadingInstallations } = useQuery({
    queryKey: ["calendar-installations", format(currentWeekStart, "yyyy-MM-dd")],
    queryFn: async () => {
      const weekEnd = addDays(currentWeekStart, 6);

      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          scheduled_date,
          status,
          task_type,
          notes,
          installer_id,
          load_id,
          orders (
            id,
            order_number,
            customers (
              first_name,
              last_name,
              company_name
            )
          ),
          cemeteries (
            name,
            city
          ),
          installation_loads (
            id,
            load_date,
            status
          )
        `)
        .is("deleted_at", null)
        .gte("scheduled_date", format(currentWeekStart, "yyyy-MM-dd"))
        .lte("scheduled_date", format(weekEnd, "yyyy-MM-dd"));

      if (error) throw error;
      
      // Fetch installer names separately
      const installerIds = [...new Set(data?.map(d => d.installer_id).filter(Boolean))];
      let installerMap: Record<string, { first_name: string | null; last_name: string | null }> = {};
      
      if (installerIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .in("id", installerIds);
        
        profiles?.forEach(p => {
          installerMap[p.id] = { first_name: p.first_name, last_name: p.last_name };
        });
      }
      
      return data?.map(d => ({
        ...d,
        profiles: d.installer_id ? installerMap[d.installer_id] || null : null
      })) as InstallationFull[];
    },
  });

  // Filter installations by installer
  const filteredInstallations = useMemo(() => {
    if (!installations) return [];
    if (selectedInstaller === "all") return installations;
    if (selectedInstaller === "unassigned") {
      return installations.filter((i) => !i.installer_id);
    }
    return installations.filter((i) => i.installer_id === selectedInstaller);
  }, [installations, selectedInstaller]);

  // Group installations by date
  const getInstallationsForDay = (day: Date) => {
    return filteredInstallations.filter((installation) => {
      const installDate = installation.installation_loads?.load_date
        ? parseISO(installation.installation_loads.load_date)
        : installation.scheduled_date
        ? parseISO(installation.scheduled_date)
        : null;

      if (!installDate) return false;
      return isSameDay(installDate, day);
    });
  };

  // Use centralized getCustomerDisplayName from formatters

  const getInstallerName = (profile: InstallationFull["profiles"]) => {
    if (!profile) return "Ei asentajaa";
    return `${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Ei asentajaa";
  };

  const isLoading = isLoadingInstallers || isLoadingInstallations;

  if (isLoading) {
    return (
      <Layout>
        <CalendarSkeleton />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <CalendarIcon className="h-8 w-8" />
              Kalenteri
            </h1>
            <p className="text-muted-foreground mt-1">
              Viikko {format(currentWeekStart, "w")} • {format(currentWeekStart, "d.M.")} - {format(addDays(currentWeekStart, 6), "d.M.yyyy")}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Select value={selectedInstaller} onValueChange={setSelectedInstaller}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Kaikki asentajat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Kaikki asentajat</SelectItem>
                <SelectItem value="unassigned">Ei asentajaa</SelectItem>
                {installers?.map((installer) => (
                  <SelectItem key={installer.id} value={installer.id}>
                    {installer.first_name} {installer.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentWeekStart(subWeeks(currentWeekStart, 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={() => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>
                Tänään
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setCurrentWeekStart(addWeeks(currentWeekStart, 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Week Grid */}
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-7 border-b">
              {weekDays.map((day, index) => {
                const isToday = isSameDay(day, new Date());
                return (
                  <div
                    key={index}
                    className={cn(
                      "p-3 text-center border-r last:border-r-0",
                      isToday && "bg-primary/5"
                    )}
                  >
                    <div className="text-sm font-medium text-muted-foreground">
                      {format(day, "EEEEEE", { locale: fi })}
                    </div>
                    <div
                      className={cn(
                        "text-lg font-semibold",
                        isToday && "text-primary"
                      )}
                    >
                      {format(day, "d")}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDays.map((day, index) => {
                const dayInstallations = getInstallationsForDay(day);
                const isToday = isSameDay(day, new Date());

                return (
                  <div
                    key={index}
                    className={cn(
                      "p-2 border-r last:border-r-0 min-h-[400px]",
                      isToday && "bg-primary/5"
                    )}
                  >
                    <div className="space-y-2">
                      {dayInstallations.map((installation) => (
                        <div
                          key={installation.id}
                          className={cn(
                            "p-2 rounded-lg border text-xs cursor-pointer hover:shadow-md transition-shadow",
                            taskTypeColors[installation.task_type] || "bg-muted"
                          )}
                        >
                          <div className="flex items-start justify-between gap-1">
                            <span className="font-medium truncate">
                              {installation.orders?.order_number}
                            </span>
                            <Badge
                              variant="outline"
                              className={cn(
                                "text-[10px] px-1 py-0",
                                INSTALLATION_STATUS_BADGE_COLORS[installation.status]
                              )}
                            >
                              {INSTALLATION_STATUS_LABELS[installation.status]?.substring(0, 3)}
                            </Badge>
                          </div>

                          <div className="mt-1 text-muted-foreground space-y-0.5">
                            <div className="flex items-center gap-1">
                              <Package className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">
                                {TASK_TYPE_LABELS[installation.task_type]}
                              </span>
                            </div>
                            {installation.cemeteries && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">
                                  {installation.cemeteries.name}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">
                                {getInstallerName(installation.profiles)}
                              </span>
                            </div>
                          </div>

                          {installation.installation_loads && (
                            <div className="mt-1 pt-1 border-t">
                              <Badge variant="secondary" className="text-[10px]">
                                Kuorma #{installation.installation_loads.id.substring(0, 8)}
                              </Badge>
                            </div>
                          )}
                        </div>
                      ))}

                      {dayInstallations.length === 0 && (
                        <div className="text-center text-muted-foreground text-xs py-4">
                          Ei asennuksia
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Selite</CardTitle>
          </CardHeader>
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Tehtävätyyppi:</span>
                {Object.entries(TASK_TYPE_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-1">
                    <div className={cn("w-3 h-3 rounded", taskTypeColors[key])} />
                    <span className="text-xs">{label}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Tila:</span>
                {Object.entries(INSTALLATION_STATUS_LABELS).map(([key, label]) => (
                  <Badge key={key} variant="outline" className={cn("text-xs", INSTALLATION_STATUS_BADGE_COLORS[key])}>
                    {label}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
